import { Auth0Provider, type AppState } from "@auth0/auth0-react";
import { PostHogProvider } from "posthog-js/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, useNavigate } from "react-router-dom";

import {
  initPostHog,
  isPostHogEnabled,
  posthog,
  readPostHogConfig,
} from "./analytics/posthog";
import { PostHogIdentify } from "./analytics/PostHogIdentify";
import { RouteAnalytics } from "./analytics/RouteAnalytics";
import { App } from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { RequireAuth } from "./components/RequireAuth";
import "./index.css";

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

interface Auth0Config {
  readonly domain: string;
  readonly clientId: string;
  readonly audience: string;
}

function readAuth0Config(): Auth0Config | null {
  const domain = import.meta.env.VITE_AUTH0_DOMAIN?.trim();
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID?.trim();
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE?.trim();
  if (!domain || !clientId || !audience) return null;
  return { domain, clientId, audience };
}

const auth0Config = readAuth0Config();

// Initialize PostHog before render. A blank VITE_POSTHOG_KEY makes this a
// no-op (no SDK init, no events) — same missing-config posture as Auth0.
initPostHog(readPostHogConfig());

function MissingAuth0EnvPanel() {
  return (
    <div
      style={{
        padding: "2rem",
        fontFamily: "system-ui, sans-serif",
        maxWidth: "640px",
        margin: "2rem auto",
        border: "1px solid #d97706",
        borderRadius: "8px",
        background: "#fef3c7",
        color: "#7c2d12",
      }}
    >
      <h1 style={{ marginTop: 0 }}>Auth0 not configured</h1>
      <p>
        The web app needs <code>VITE_AUTH0_DOMAIN</code>,{" "}
        <code>VITE_AUTH0_CLIENT_ID</code>, and <code>VITE_AUTH0_AUDIENCE</code>{" "}
        to start. See <code>apps/web/.env.example</code> for guidance.
      </p>
    </div>
  );
}

// Auth0Provider's onRedirectCallback needs router navigation to restore
// the pre-login URL — but useNavigate only works inside BrowserRouter.
// This wrapper lets us mount BrowserRouter outermost while still passing
// a navigate-aware callback to Auth0Provider.
function Auth0ProviderWithNavigate({
  config,
  children,
}: {
  config: Auth0Config;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const onRedirectCallback = (appState: AppState | undefined) => {
    const returnTo = appState?.returnTo;
    if (typeof returnTo === "string" && returnTo.startsWith("/")) {
      void navigate(returnTo, { replace: true });
    }
  };
  return (
    <Auth0Provider
      domain={config.domain}
      clientId={config.clientId}
      authorizationParams={{
        audience: config.audience,
        redirect_uri: window.location.origin,
      }}
      useRefreshTokens
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
}

function AuthedApp({ config }: { config: Auth0Config }) {
  const tree = (
    <BrowserRouter>
      <Auth0ProviderWithNavigate config={config}>
        <ErrorBoundary>
          <RequireAuth>
            <PostHogIdentify />
            <RouteAnalytics />
            <App />
          </RequireAuth>
        </ErrorBoundary>
      </Auth0ProviderWithNavigate>
    </BrowserRouter>
  );
  // Only mount PostHogProvider when the SDK actually initialized. With a
  // blank VITE_POSTHOG_KEY (local dev / preview) the provider would hand
  // components an uninitialized client and log "called before init" on
  // every usePostHog().capture(...) — keep the blank-key path a true no-op.
  return isPostHogEnabled() ? (
    <PostHogProvider client={posthog}>{tree}</PostHogProvider>
  ) : (
    tree
  );
}

createRoot(rootElement).render(
  <StrictMode>
    {auth0Config ? (
      <AuthedApp config={auth0Config} />
    ) : (
      <MissingAuth0EnvPanel />
    )}
  </StrictMode>,
);
